import { TileStyle, PipPosition } from './types';
import { TARGET_TILE_AR, COVER_FIT } from './constant';

/** Fits a 16:9 screen into the usable area using contain (letterbox) sizing. */
export function getContainedScreenRect(
  W: number,
  H: number,
  margin: number
): { screenTop: number; screenLeft: number; screenW: number; screenH: number; usableW: number; usableH: number } {
  const usableW = W - 2 * margin;
  const usableH = H - 2 * margin;
  const screenW = Math.min(usableW, usableH * TARGET_TILE_AR);
  const screenH = screenW / TARGET_TILE_AR;
  const screenTop = margin + Math.max(0, (usableH - screenH) / 2);
  const screenLeft = margin + Math.max(0, (usableW - screenW) / 2);
  return { screenTop, screenLeft, screenW, screenH, usableW, usableH };
}

export const getSpeakerLayoutBorderKey = (value: boolean) => (value ? 'with-border' : 'without-border');

export const getParticipantIndex = (layoutType: 'GRID' | 'FOCUS', totalParticipant: number): number => {
  return (layoutType === 'FOCUS' ? 1 : 0) + totalParticipant;
};

/** Create a tile using container query units (cqw / cqh).
 *  Requires `container-type: size` on the nearest stageContent ancestor. */
export function tile(
  topPx: number,
  leftPx: number,
  widthPx: number,
  heightPx: number,
  containerW: number,
  containerH: number,
  radius: string,
  extra: Record<string, any> = {}
): TileStyle {
  const cqw = (v: number) => `${(v / containerW) * 100}cqw`;
  const cqh = (v: number) => `${(v / containerH) * 100}cqh`;
  return {
    style: { ...COVER_FIT },
    containerStyle: {
      overflow: 'hidden' as const,
      borderRadius: radius,
      position: 'absolute' as const,
      top: cqh(topPx),
      left: cqw(leftPx),
      width: cqw(widthPx),
      height: cqh(heightPx),
      ...extra
    }
  };
}

/** Create a tile using raw string values (for PIP special positioning). */
export function tileRaw(
  top: string,
  left: string,
  width: string,
  height: string,
  radius: string,
  extra: Record<string, any> = {}
): TileStyle {
  return {
    style: { ...COVER_FIT },
    containerStyle: {
      overflow: 'hidden' as const,
      borderRadius: radius,
      position: 'absolute' as const,
      top,
      left,
      width,
      height,
      ...extra
    }
  };
}

/**
 * Returns info about the last ("remainder") row when it has fewer columns
 * than the row above it — e.g. [2,1] for 3 speakers.
 * Remainder tiles are sized to match the preceding row and are centred.
 */
export function incompleteRow(
  dist: number[],
  gap: number,
  usableW: number
): { lastIdx: number; isIncomplete: boolean; tileW: number } {
  const lastIdx = dist.length - 1;
  const lastCols = dist[lastIdx];
  const prevCols = dist.length > 1 ? dist[lastIdx - 1] : lastCols;
  const isIncomplete = lastCols < prevCols;
  // Size remainder tiles as if the fuller preceding row continued
  const tileW = isIncomplete ? (usableW - (prevCols - 1) * gap) / prevCols : 0;
  return { lastIdx, isIncomplete, tileW };
}

export /**
 * Renders a grid of speaker tiles into absolute-positioned TileStyle objects.
 * Shared by computeTiles (landscape, portrait, square) and computePortraitScreenShare.
 *
 * @param dist       Columns-per-row distribution (e.g. [3, 3] for 6 speakers)
 * @param gap        Pixel gap between tiles (both axes)
 * @param usableW    Available grid width in pixels
 * @param containerH Available grid height in pixels
 * @param baseY      Top of the grid area in stage pixels (before centering)
 * @param margin     Left inset in stage pixels
 * @param W / H      Full stage dimensions (for % conversion)
 * @param radius     CSS border-radius string
 * @param scaleCap   Max scale factor: 1 = keep natural row heights within the container
 * @param tileAspectRatio Width:height ratio used to derive each row's natural height
 */
function buildSpeakerGrid(
  dist: number[],
  gap: number,
  usableW: number,
  containerH: number,
  baseY: number,
  margin: number,
  W: number,
  H: number,
  radius: string,
  scaleCap: number,
  verticalAlign: 'center' | 'top' = 'center',
  tileAspectRatio: number = TARGET_TILE_AR
): TileStyle[] {
  const rowCount = dist.length;
  const vGaps = (rowCount - 1) * gap;
  const { lastIdx, isIncomplete: lastIsIncomplete, tileW: incompleteTileW } = incompleteRow(dist, gap, usableW);

  const naturalHeights = dist.map((cols, i) => {
    const tw = i === lastIdx && lastIsIncomplete ? incompleteTileW : (usableW - (cols - 1) * gap) / cols;
    return tw / tileAspectRatio;
  });

  const sumH = naturalHeights.reduce((a, b) => a + b, 0);
  const scale = Math.min(scaleCap, (containerH - vGaps) / sumH);
  const totalGridH = sumH * scale + vGaps;

  let topOffset = verticalAlign === 'top' ? baseY : baseY + Math.max(0, (containerH - totalGridH) / 2);
  const tiles: TileStyle[] = [];

  for (let r = 0; r < rowCount; r++) {
    const cols = dist[r];
    const incomplete = r === lastIdx && lastIsIncomplete;
    const cellW = incomplete ? incompleteTileW : (usableW - (cols - 1) * gap) / cols;
    const cellH = naturalHeights[r] * scale;
    const rowW = cols * cellW + (cols - 1) * gap;
    const leftOffset = incomplete ? margin + (usableW - rowW) / 2 : margin;

    for (let c = 0; c < cols; c++) {
      tiles.push(tile(topOffset, leftOffset + c * (cellW + gap), cellW, cellH, W, H, radius));
    }
    topOffset += cellH + gap;
  }

  return tiles;
}

/**
 * Computes pixel top/left for a RECTANGULAR PIP overlay given a reference rect.
 * refTop/refLeft/refW/refH describe the anchor area (usable area or screen rect).
 */
export function pipRectPosition(
  pos: PipPosition,
  pipW: number,
  pipH: number,
  refTop: number,
  refLeft: number,
  refW: number,
  refH: number,
  W: number,
  H: number
): { pipTop: number; pipLeft: number } {
  const VMARGIN = H * 0.03;
  const HMARGIN = W * 0.02;

  let pipTop: number;
  if (pos === 'LEFT_TOP' || pos === 'RIGHT_TOP' || pos === 'TOP_MIDDLE') {
    pipTop = refTop + VMARGIN;
  } else if (pos === 'LEFT_BOTTOM' || pos === 'RIGHT_BOTTOM' || pos === 'BOTTOM_MIDDLE') {
    pipTop = refTop + refH - pipH - VMARGIN;
  } else {
    pipTop = refTop + (refH - pipH) / 2;
  }

  let pipLeft: number;
  if (pos === 'LEFT_TOP' || pos === 'LEFT_BOTTOM' || pos === 'LEFT_MIDDLE') {
    pipLeft = refLeft + HMARGIN;
  } else if (pos === 'RIGHT_TOP' || pos === 'RIGHT_BOTTOM' || pos === 'RIGHT_MIDDLE') {
    pipLeft = refLeft + refW - pipW - HMARGIN;
  } else {
    pipLeft = refLeft + (refW - pipW) / 2;
  }

  return { pipTop, pipLeft };
}

/**
 * Returns extra CSS properties to pass to tileRaw() for a CIRCULAR PIP overlay.
 * The returned object overrides top/left/bottom/right/transform in containerStyle.
 */
export function pipCircularPositionExtra(pos: PipPosition): Record<string, string> {
  const extra: Record<string, string> = {};

  if (pos === 'LEFT_TOP' || pos === 'RIGHT_TOP' || pos === 'TOP_MIDDLE') {
    extra.top = '2.5cqh';
    extra.bottom = 'auto';
  } else if (pos === 'LEFT_BOTTOM' || pos === 'RIGHT_BOTTOM' || pos === 'BOTTOM_MIDDLE') {
    extra.top = 'auto';
    extra.bottom = '2.5cqh';
  } else {
    extra.top = '50cqh';
    extra.bottom = 'auto';
    extra.transform = 'translateY(-50%)';
  }

  if (pos === 'LEFT_TOP' || pos === 'LEFT_BOTTOM' || pos === 'LEFT_MIDDLE') {
    extra.left = '2cqw';
    extra.right = 'auto';
  } else if (pos === 'RIGHT_TOP' || pos === 'RIGHT_BOTTOM' || pos === 'RIGHT_MIDDLE') {
    extra.left = 'auto';
    extra.right = '2cqw';
  } else {
    extra.left = '50cqw';
    extra.right = 'auto';
    extra.transform = extra.transform ? `${extra.transform} translateX(-50%)` : 'translateX(-50%)';
  }

  return extra;
}

export const tileStyleAt = (index: number, isScreen: boolean, tiles: Array<TileStyle>): TileStyle => {
  const hidden = { style: { display: 'none' as const }, containerStyle: { display: 'none' as const } } as any;

  if (!tiles?.length) {
    return hidden;
  }

  return isScreen ? (tiles[0] ?? hidden) : (tiles[index] ?? hidden);
};
